import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const emailSchema = z.string().trim().email().max(320);

export interface FoundersProgress {
  goal: number;
  claimed: number;
  remaining: number;
  closed: boolean;
}

export interface FounderWallEntry {
  display_name: string;
  handle: string | null;
  created_at: string;
}

function codeFor(): string {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  const bytes = crypto.getRandomValues(new Uint8Array(6));
  let out = "";
  for (const b of bytes) out += alphabet[b % alphabet.length];
  return `MOVE-${out}`;
}

export const getFoundersProgress = createServerFn({ method: "GET" }).handler(
  async (): Promise<FoundersProgress> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await supabaseAdmin.rpc("founders_progress");
    const row = (data ?? {}) as { goal?: number; claimed?: number };
    const goal = row.goal ?? 400;
    const claimed = row.claimed ?? 0;
    return { goal, claimed, remaining: Math.max(goal - claimed, 0), closed: claimed >= goal };
  },
);

export const getFoundersWall = createServerFn({ method: "GET" }).handler(
  async (): Promise<FounderWallEntry[]> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await supabaseAdmin.rpc("founders_wall");
    return (data ?? []) as FounderWallEntry[];
  },
);

export const reserveFounderSpot = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({
        email: emailSchema,
        displayName: z.string().trim().min(1).max(120),
        handle: z.string().trim().max(120).optional().nullable(),
        showPublicly: z.boolean().default(true),
        note: z.string().trim().max(2000).optional().nullable(),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const { callerKey, hashedKey, rateLimit } = await import("./rate-limit.server");
    const [byIp, byEmail] = await Promise.all([
      rateLimit("founder_reserve:ip", callerKey("founder"), 8, 3600),
      rateLimit("founder_reserve:email", hashedKey(data.email), 3, 3600),
    ]);
    if (!byIp.allowed || !byEmail.allowed) {
      return { ok: false as const, message: "Too many attempts. Try again in a little while." };
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: progress } = await supabaseAdmin.rpc("founders_progress");
    const p = (progress ?? {}) as { goal?: number; claimed?: number };
    if ((p.claimed ?? 0) >= (p.goal ?? 400)) {
      return { ok: false as const, message: "The founding round is closed. Join the waitlist below." };
    }


    const { error } = await supabaseAdmin.from("founder_reservations").insert({
      email: data.email.toLowerCase(),
      display_name: data.displayName,
      handle: data.handle || null,
      show_publicly: data.showPublicly,
      note: data.note || null,
    });

    if (error) {
      if (error.code === "23505") {
        return { ok: true as const, message: "You're already on the founders list — we have your spot." };
      }
      return { ok: false as const, message: "Something went wrong. Try again in a moment." };
    }

    return { ok: true as const, message: "Your founder spot is held. We'll email you when the round opens." };
  });

export const requestDiscountCode = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => z.object({ email: emailSchema }).parse(input))
  .handler(async ({ data }) => {
    const { callerKey, hashedKey, rateLimit } = await import("./rate-limit.server");
    const [byIp, byEmail] = await Promise.all([
      rateLimit("discount:ip", callerKey("discount"), 10, 3600),
      rateLimit("discount:email", hashedKey(data.email), 5, 86400),
    ]);
    if (!byIp.allowed || !byEmail.allowed) {
      return { ok: false as const, message: "Too many requests. Try again in a little while." };
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const email = data.email.toLowerCase();
    const percent = 20;


    const { data: existing } = await supabaseAdmin
      .from("discount_codes")
      .select("code, percent, expires_at")
      .eq("email", email)
      .gt("expires_at", new Date().toISOString())
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (existing) {
      return {
        ok: true as const,
        code: existing.code,
        percent: existing.percent,
        expiresAt: existing.expires_at,
        reused: true,
      };
    }

    const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
    for (let attempt = 0; attempt < 5; attempt++) {
      const code = codeFor();
      const { error } = await supabaseAdmin
        .from("discount_codes")
        .insert({ code, email, percent, expires_at: expiresAt });
      if (!error) {
        return { ok: true as const, code, percent, expiresAt, reused: false };
      }
      if (error.code !== "23505") break;
    }

    return { ok: false as const, message: "Couldn't issue a code right now. Try again shortly." };
  });

/**
 * Validates a code the way a checkout would: the code must exist, still be
 * inside its 24-hour window, not already redeemed, and belong to the email
 * that requested it. Read-only — redeeming happens in the admin panel.
 */
export const checkDiscountCode = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({ code: z.string().trim().min(3).max(32), email: emailSchema })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const { callerKey, rateLimit } = await import("./rate-limit.server");
    const verdict = await rateLimit("discount_check:ip", callerKey("discount_check"), 20, 3600);
    if (!verdict.allowed) {
      return { valid: false as const, reason: "Too many attempts. Try again shortly." };
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row } = await supabaseAdmin
      .from("discount_codes")
      .select("code, email, percent, expires_at, redeemed_at")
      .eq("code", data.code.trim().toUpperCase())
      .maybeSingle();

    if (!row || row.email !== data.email.trim().toLowerCase()) {
      return { valid: false as const, reason: "That code doesn't match this email address." };
    }
    if (row.redeemed_at) return { valid: false as const, reason: "That code has already been used." };
    if (new Date(row.expires_at).getTime() <= Date.now()) {
      return { valid: false as const, reason: "That code has expired." };
    }
    return { valid: true as const, percent: row.percent, expiresAt: row.expires_at };
  });

/**
 * Bulk/team licence quote requests. Server-side so submissions are rate
 * limited and normalised before they land in the table the admin panel reads.
 */
export const submitBulkInquiry = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({
        company: z.string().trim().min(1).max(200),
        contactName: z.string().trim().min(1).max(200),
        email: emailSchema,
        seats: z.coerce.number().int().min(1).max(100000),
        message: z.string().trim().max(4000).optional().nullable(),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const { callerKey, hashedKey, rateLimit } = await import("./rate-limit.server");
    const [byIp, byEmail] = await Promise.all([
      rateLimit("bulk_inquiry:ip", callerKey("bulk"), 5, 3600),
      rateLimit("bulk_inquiry:email", hashedKey(data.email), 3, 86400),
    ]);
    if (!byIp.allowed || !byEmail.allowed) {
      return { ok: false as const, message: "Too many requests. Try again in a little while." };
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin.from("bulk_inquiries").insert({
      company: data.company,
      contact_name: data.contactName,
      email: data.email.toLowerCase(),
      seats: data.seats,
      message: data.message || null,
    });
    if (error) return { ok: false as const, message: "Couldn't send that. Try again in a moment." };
    return { ok: true as const };
  });

/**
 * Lightweight "Teams — coming soon" interest capture. Stores intent for
 * collaboration features without building the full team layer yet.
 */
export const submitTeamInterest = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({
        email: emailSchema,
        company: z.string().trim().max(200).optional().nullable(),
        teamSize: z.coerce.number().int().min(1).max(100000).optional().nullable(),
        useCase: z.string().trim().max(4000).optional().nullable(),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const { callerKey, hashedKey, rateLimit } = await import("./rate-limit.server");
    const [byIp, byEmail] = await Promise.all([
      rateLimit("team_interest:ip", callerKey("team_interest"), 5, 3600),
      rateLimit("team_interest:email", hashedKey(data.email), 2, 86400),
    ]);
    if (!byIp.allowed || !byEmail.allowed) {
      return { ok: false as const, message: "Too many requests. Try again in a little while." };
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin.from("team_interest").insert({
      email: data.email.toLowerCase(),
      company: data.company || null,
      team_size: data.teamSize || null,
      use_case: data.useCase || null,
    });
    if (error) return { ok: false as const, message: "Couldn't send that. Try again in a moment." };
    return { ok: true as const };
  });
